import { FormattedDate } from 'react-intl';
import { Chip, Grid, Paper, styled, type PaperProps } from '@mui/material';
import { TimelineConnector, TimelineContent, TimelineDot, TimelineItem, TimelineSeparator } from '@mui/lab';

import { type LogsType } from 'app/main/components/hooks/useLogs';
import useSettings from 'app/main/components/hooks/useSettings';
import { parseDate } from 'app/main/components/Utils';
import Icon from 'app/main/components/Icon';
import Text from 'app/main/components/Text';
import SquareChip from 'app/main/components/SquareChip';

interface FlowItemProps {
	module: string;
	name: string;
	uid: string;
	title: string;
	internalNumber: string;
	date: string;
	deleted: boolean;
	current: boolean;
	isLast: boolean;
	logProps?: LogsType;
}

const StyledTimelineItem = styled(TimelineItem)(({ theme }) => ({
	minHeight: 0,
	'&::before': {
		width: 0,
		maxWidth: 0,
		padding: 0
	},
	'& > .MuiTimelineContent-root ': {
		paddingTop: 0,
		paddingLeft: theme.spacing(1),
		paddingRight: theme.spacing(1)
	},
	'&:not(:last-child) > .MuiTimelineContent-root ': {
		paddingBottom: theme.spacing(2)
	}
}));

const LogsPaper = styled((props: PaperProps & { hasLogs?: boolean }) => {
	const { hasLogs, ...other } = props;
	return <Paper {...other} />;
})(({ hasLogs, theme }) => ({
	'&': hasLogs && {
		padding: 8,
		paddingRight: 12,
		borderRadius: 4,
		backgroundColor: theme.palette.action.selected,
		cursor: 'pointer',
		'&:hover': {
			backgroundColor: theme.palette.action.focus
		}
	}
}));

export default function FlowItem(props: FlowItemProps) {
	const { settings, t } = useSettings(props.module);

	const moduleTitle = t(`MODULE_${props.module}_NAME_SINGULAR`);

	const hasLogs = props.logProps?.hasLogs(props.name);
	const logsCounter = props.logProps?.getLogsCounter(props.name);
	const openLogsDialog = () => props.logProps?.openLogsDialog(props.name, t(`MODULE_${props.module}_NAME_SINGULAR`));

	return (
		<StyledTimelineItem>
			<TimelineSeparator>
				<TimelineDot className="my-4 border-none shadow-0" variant="outlined">
					<Icon
						icon={props.deleted ? 'delete-alert' : settings.modu_icon}
						size={24}
						color={props.deleted ? 'warning.main' : props.current ? 'secondary.main' : 'action.active'}
					/>
				</TimelineDot>
				{!props.isLast && <TimelineConnector />}
			</TimelineSeparator>
			<TimelineContent>
				<LogsPaper elevation={0} hasLogs={hasLogs} onClick={openLogsDialog}>
					<Grid container justifyContent="space-between" alignItems="center">
						<Grid size="grow">
							<Text
								className="pb-2"
								variant="subtitle2"
								color={props.deleted ? 'warning.main' : props.current ? 'secondary' : 'text.primary'}
								to={!hasLogs && !props.current ? { module: props.module, uid: props.uid } : undefined}
								noWrap={false}
							>
								{`${moduleTitle} - `}
								{props.internalNumber && `${props.internalNumber} - `}
								{props.title}
							</Text>
							{props.date ? (
								<Text variant="subtitle2" color="text.secondary">
									<FormattedDate
										value={parseDate(props.date)?.toDate()}
										year="numeric"
										month="long"
										day="numeric"
									/>
								</Text>
							) : (
								<Text className="italic" variant="subtitle2" color="text.disabled">
									{t('VIEW_ITEM_NO_DATE')}
								</Text>
							)}
						</Grid>
						{hasLogs && (
							<Grid>
								<Chip label={logsCounter} size="small" color="primary" />
							</Grid>
						)}
					</Grid>
				</LogsPaper>
			</TimelineContent>
		</StyledTimelineItem>
	);
}
